/**
 * FRIDGE-1434 — head pages lead with the singular a shopper types.
 *
 * Measured (Semrush, Google US, 10/05/2026 tracking and the 07/08/2026
 * positions export): "kegerator" 27,100 searches a month against
 * "kegerators" 1,900; "freezer" 74,000 against "freezers" 12,100; and
 * neither /kegerators nor /collections/freezers held a top-100 position for
 * the singular or the plural, while both pages are live, indexable and
 * self-canonical. Both led with the plural in the title and the H1.
 *
 * This module is the ONE place the two head pages take their H1, their
 * <title> keyword and benefit, and the answer-first sentence's noun from.
 * The hub route (app/[hub-slug]/page.tsx, hub-specialty.tsx) and the
 * collection route (app/collections/[slug]/collection-page-render.tsx and
 * its client) read it; nothing retypes the words. Titles compose through
 * composePageTitle, so the shared length policy (FRIDGE-1138) still governs.
 *
 * The answer-first sentence states the page's own numbers (the model count
 * and the median lowest price the page already computes), never a retyped
 * figure (the FRIDGE-1435 class).
 */
import { composePageTitle } from '@/lib/seo/page-title'

export interface HeadTermHeadline {
  /** The singular noun the sentence and the keyword lead with ("Kegerator"). */
  term: string
  /** The H1. Leads with the singular; the ':' qualifier names the page. */
  h1: string
  /** The <title> keyword. */
  keyword: string
  /** The <title> benefit (the searcher's goal on this page). */
  benefit: string
}

const HUBS: Record<string, HeadTermHeadline> = {
  kegerators: {
    term: 'Kegerator',
    h1: 'Kegerator Prices & Deals: Keg Fridges & Beer Dispensers',
    keyword: 'Kegerator Prices & Deals',
    benefit: 'Keg Fridges & Beer Dispensers',
  },
}

const COLLECTIONS: Record<string, HeadTermHeadline> = {
  freezers: {
    term: 'Freezer',
    h1: 'Freezer Prices: Chest, Upright & Compact Compared',
    keyword: 'Freezer Prices',
    benefit: 'Chest, Upright & Compact Compared',
  },
}

/**
 * FRIDGE-1483 — the base width pages. Their use-case variants
 * (`NN-inch-wide-refrigerators-for-<use case>`) carried no filter and split the
 * width queries across five to seven URLs ("32 inch wide refrigerator" 1,600 a
 * month, positions 19 to 30, Semrush 10/13/2026); each variant now 308s to its
 * base page, and the base page leads with the singular the shopper types and
 * an answer-first sentence that states the page's own count and median plus
 * the Dimensions Report's standard-width figures (widthTermIntro).
 */
export const WIDTH_COLLECTION_INCHES = [24, 28, 29, 30, 31, 32, 33, 34, 35] as const

const WIDTH_COLLECTIONS: Record<string, HeadTermHeadline> = Object.fromEntries(
  WIDTH_COLLECTION_INCHES.map((inches) => [
    `${inches}-inch-wide-refrigerators`,
    {
      term: `${inches} Inch Wide Refrigerator`,
      h1: `${inches} Inch Wide Refrigerator Prices: Sizes & Models Compared`,
      keyword: `${inches} Inch Wide Refrigerator Prices`,
      benefit: 'Sizes & Models',
    },
  ])
)

/** The width, in inches, of a base width collection slug this module names; null otherwise. */
export function widthCollectionInches(slug: string): number | null {
  const m = /^(\d+)-inch-wide-refrigerators$/.exec(slug)
  if (!m) return null
  const inches = Number(m[1])
  return (WIDTH_COLLECTION_INCHES as readonly number[]).includes(inches) ? inches : null
}

/** The head-term headline for a hub page (/<slug>), or null. */
export function hubHeadline(slug: string): HeadTermHeadline | null {
  return HUBS[slug] ?? null
}

/** The head-term headline for a collection page (/collections/<slug>), or null. */
export function collectionHeadline(slug: string): HeadTermHeadline | null {
  return COLLECTIONS[slug] ?? WIDTH_COLLECTIONS[slug] ?? null
}

/** Every head-term headline, for the tests and the title census. */
export function allHeadTermHeadlines(): Array<{ route: string; headline: HeadTermHeadline }> {
  return [
    ...Object.entries(HUBS).map(([slug, headline]) => ({ route: `/${slug}`, headline })),
    ...Object.entries(COLLECTIONS).map(([slug, headline]) => ({ route: `/collections/${slug}`, headline })),
    ...Object.entries(WIDTH_COLLECTIONS).map(([slug, headline]) => ({ route: `/collections/${slug}`, headline })),
  ]
}

/**
 * The page <title> under the shared policy: keyword | benefit | Fridge.com.
 * `page` (FRIDGE-1685): a listing's page 2 and later carry the page marker
 * inside the budget.
 */
export function headTermTitle(headline: HeadTermHeadline, page?: number | null): string {
  return composePageTitle({ keyword: headline.keyword, benefit: headline.benefit, page })
}

export interface HeadTermIntroFacts {
  term: string
  /** Tracked models on the page; a floor when `countIsFloor` is true. */
  models: number
  countIsFloor?: boolean
  /** Median lowest tracked price, dollars (null when unpublished). */
  medianPrice: number | null
  minPrice?: number | null
  maxPrice?: number | null
}

function dollars(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`
}

/**
 * The answer-first sentence a head page opens with. It names the singular,
 * states the count and the median, and says what the page does. Null when
 * there is nothing measured to state, so the caller keeps its own fallback.
 */
export function headTermIntro(facts: HeadTermIntroFacts): string | null {
  if (!(facts.models > 0)) return null
  const noun = facts.term.toLowerCase()
  const plural = `${noun}s`
  const count = `${facts.countIsFloor ? 'over ' : ''}${facts.models.toLocaleString('en-US')}`
  const head = `${facts.term} prices:`
  if (facts.medianPrice == null || !(facts.medianPrice > 0)) {
    return `${head} ${count} ${facts.models === 1 && !facts.countIsFloor ? noun : plural} tracked by Fridge.com, with prices compared across major online appliance retailers.`
  }
  const range =
    facts.minPrice != null && facts.maxPrice != null && facts.maxPrice > facts.minPrice
      ? `, with models from ${dollars(facts.minPrice)} to ${dollars(facts.maxPrice)}`
      : ''
  return `${head} the median lowest price across ${count} ${plural} tracked by Fridge.com is ${dollars(facts.medianPrice)}${range}. Compare prices from major online appliance retailers.`
}

/**
 * FRIDGE-1483 — the Dimensions Report facts a base width page states beside
 * its own count and median. The server reads them from the committed
 * snapshot (app/data/_data/dimensions.json, through app/data/dimensions);
 * lib never imports app, so they arrive as plain values.
 */
export interface WidthAnswerFacts {
  /** The collection's recorded-width window, inches (lib/collection-filters WIDTH_RANGES). */
  widthMin: number
  widthMax: number
  /** Median recorded width of the report's full-size refrigerators, inches. */
  medianFullSizeWidth: number
  /** The most common width band, in the site's words ("32-to-35.99-inch"). */
  bandLabel: string
  /** Whole-percent share of full-size models in that band, and the counts behind it. */
  bandSharePct: number
  bandN: number
  fullSizeN: number
  /** "July 2026": the snapshot's computed_at, month and year. */
  analyzedLabel: string
}

/**
 * The answer-first sentence a base width page opens with: the page's own
 * count and median at its recorded-width window, then the standard-width
 * answer from the Dimensions Report. Null when nothing is measured.
 */
export function widthTermIntro(facts: HeadTermIntroFacts & WidthAnswerFacts & { inches: number }): string | null {
  if (!(facts.models > 0)) return null
  const count = `${facts.countIsFloor ? 'over ' : ''}${facts.models.toLocaleString('en-US')}`
  const noun = `${facts.inches}-inch wide refrigerator`
  const plural = facts.models === 1 && !facts.countIsFloor ? noun : `${noun}s`
  const head = `${noun} prices:`
  const scope = `at a recorded width of ${facts.widthMin} to ${facts.widthMax} inches`
  const scale =
    `For scale, the median full-size refrigerator measures ${facts.medianFullSizeWidth} inches wide, and ` +
    `${facts.bandSharePct}% of the ${facts.fullSizeN.toLocaleString('en-US')} full-size models in the Fridge.com ` +
    `Refrigerator Dimensions Report (analyzed in ${facts.analyzedLabel}) sit in the ${facts.bandLabel} band.`
  if (facts.medianPrice == null || !(facts.medianPrice > 0)) {
    return `${head} ${count} ${plural} tracked by Fridge.com ${scope}, with prices compared across major online appliance retailers. ${scale}`
  }
  const range =
    facts.minPrice != null && facts.maxPrice != null && facts.maxPrice > facts.minPrice
      ? `, with models from ${dollars(facts.minPrice)} to ${dollars(facts.maxPrice)}`
      : ''
  return `${head} the median lowest price across ${count} ${plural} tracked by Fridge.com ${scope} is ${dollars(facts.medianPrice)}${range}. ${scale} Compare prices from major online appliance retailers.`
}
